import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Car, Wrench, Clock, ShieldCheck } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useSeo, breadcrumbs, offerList, localBusiness } from "@/lib/seo";
import { PublicShell, Crumbs } from "@/components/PublicShell";
import { ShareBar } from "@/components/ShareBar";
import { NotifyMe } from "@/components/NotifyMe";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const ALT = {
  wash_at_work: "A car being hand washed with a foam lance in a workplace car park",
  mobile_van: "A mobile car valeting van with its doors open, vacuum running beside a clean car",
  pick_up_drop_off: "Car keys being handed over at a reception desk for a wash and return",
  vacuum_valet: "The inside of a car being vacuumed, mats out and seats spotless",
  checkup: "A car up on a two post lift in a clean garage workshop for a routine check-up",
  extras: "A machine polisher working wax into a car bonnet to a deep shine",
  ev_charge: "A freshly washed electric car plugged into a charging post in a workplace car park",
};

export default function CarCarePublic() {
  const [board, setBoard] = useState(null);

  useEffect(() => {
    api.get("/car/public").then(({ data }) => setBoard(data)).catch(() => setBoard({ sections: [] }));
  }, []);

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      breadcrumbs([["Home", "/"], ["Car care", "/car-care"]]),
      localBusiness({
        name: "Unity & Utility car care at work",
        description: "Car wash, mobile valeting and garage check-ups booked online and done while you are at work — washed in the car park, collected and returned, or a mobile van with a vacuum on the way home.",
        path: "/car-care",
        image: board?.image_url ? `${BACKEND}${board.image_url}` : undefined,
        type: "AutoWash",
        area: "Worldwide",
      }),
      offerList("Car care you can book while you are at work", "/car-care",
        (board?.businesses || []).flatMap((b) => (b.prices || []).map((p) => ({
          name: `${p.label} — ${b.name}`,
          description: `${b.kind}${b.where ? ` in ${b.where}` : ""}`,
          image: b.image_url ? `${BACKEND}${b.image_url}` : undefined,
          price: p.from, symbol: b.symbol, unit: "per car",
        })))),
    ],
  };

  useSeo({
    title: "Car wash at work — washed while you are on shift, or a mobile van on the way home | Unity & Utility",
    description: "Book a car wash, interior vacuum, valet or routine garage check-up online and have it done while you are at work. Washed where it is parked, keys collected and the car brought back clean, or a mobile van with a vacuum catching you on the way home. Before and after photographs on every job, prices set by the owner, pay online or on handover.",
    path: "/car-care",
    image: board?.image_url ? `${BACKEND}${board.image_url}` : undefined,
    jsonLd,
  });

  return (
    <PublicShell>
      <div className="mx-auto max-w-5xl px-5 py-10 sm:px-8 sm:py-14" data-testid="car-public-page">
        <Crumbs items={[["Home", "/"], ["Car care", "/car-care"]]} />
        <p className="overline text-[#B3241B]">Car care at work</p>
        <h1 className="font-display mt-2 flex items-start gap-3 text-4xl font-bold leading-[1.05] tracking-tight text-[#0F172A] sm:text-5xl">
          <Car size={34} weight="duotone" className="mt-1 shrink-0 text-[#002FA7]" />
          Get it washed while you work
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-700" data-testid="car-public-intro">
          Leave the car where it is parked and come back to it clean. Washers work along the
          company car park while you are on shift, drivers collect the keys and bring the car
          back valeted, and mobile vans with a vacuum on board catch you at work or at home on
          the way back. Garages take the routine stuff too — the service, the inspection, oil,
          tyres, brakes and battery — collected from work and returned the same day where they
          can. Owners publish their own rounds and prices, every finished job carries a before
          and an after photograph, and a check-up sets its own reminder for next time. Pictures
          on this board are redrawn by AI every 24 hours.
        </p>
        <div className="mt-5">
          <ShareBar
            url={board?.share_token
              ? `${window.location.origin}/p/${board.share_token}`
              : `${window.location.origin}/car-care`}
            text="Get the car washed and checked over while you are at work — or let the van come to you."
            testid="car-public-share" instagram marketplace />
        </div>

        {board?.image_url && (
          <img src={`${BACKEND}${board.image_url}`} alt={ALT.wash_at_work} loading="lazy"
            className="mt-8 h-56 w-full border border-border object-cover sm:h-80"
            data-testid="car-public-image" />
        )}

        <section className="mt-6 border border-[#002FA7] bg-[#EEF3FF] p-5" data-testid="car-public-card">
          <p className="overline text-[#002FA7]">
            Today{board?.day ? ` · ${board.day}` : ""}
            {board?.auto_posted && <span className="ml-2">Posted to Facebook</span>}
          </p>
          <div className="mt-3 grid grid-cols-3 gap-3">
            {[["Washes and garages", board?.businesses_count, "car-stat-businesses"],
              ["Rounds running", board?.rounds_count, "car-stat-rounds"],
              ["Jobs finished", board?.done_count, "car-stat-done"]].map(([l, v, t]) => (
              <div key={t} className="border border-border bg-white p-3" data-testid={t}>
                <p className="font-display text-2xl font-bold text-[#0F172A]">{v ?? 0}</p>
                <p className="text-xs text-slate-500">{l}</p>
              </div>
            ))}
          </div>
        </section>

        {(board?.sections || []).map((s) => (
          <section key={s.key} className="mt-10" data-testid={`car-public-section-${s.key}`}>
            <h2 className="font-display text-base font-bold tracking-tight text-[#0F172A] md:text-lg">
              {s.label}
            </h2>
            {board?.section_images?.[s.key] && (
              <img src={`${BACKEND}${board.section_images[s.key]}`} alt={ALT[s.key] || s.label}
                loading="lazy" className="mt-3 h-44 w-full border border-border object-cover sm:h-56"
                data-testid={`car-public-image-${s.key}`} />
            )}
            <div className="mt-3">
              <ShareBar url={`${window.location.origin}/car-care`}
                text={`${s.label} — booked online and done while you are at work.`}
                testid={`car-public-share-${s.key}`} compact instagram />
            </div>
          </section>
        ))}

        <section className="mt-10" data-testid="car-public-services">
          <h2 className="font-display text-base font-bold tracking-tight text-[#0F172A] md:text-lg">
            Everything you can book
          </h2>
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            {(board?.services || []).map((s, i) => (
              <div key={s.key} className="border border-border bg-white p-4"
                data-testid={`car-public-service-${i}`}>
                <p className="font-semibold text-[#0F172A]">{s.label}</p>
                <p className="mt-1 text-xs text-slate-500">{s.where}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-10" data-testid="car-public-rounds">
          <h2 className="font-display flex items-center gap-2 text-base font-bold tracking-tight text-[#0F172A] md:text-lg">
            <Clock size={18} weight="duotone" className="text-[#002FA7]" />
            Rounds this week
          </h2>
          {(board?.rounds || []).length === 0 ? (
            <p className="mt-2 text-sm text-slate-600" data-testid="car-public-rounds-empty">
              No rounds published round here yet. If you run a wash, a valeting van or a garage,
              put your business up and publish the days you come by — staff then just pick a slot.
            </p>
          ) : (
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {board.rounds.map((r, i) => (
                <div key={i} className="border border-border bg-white p-4" data-testid={`car-public-round-${i}`}>
                  <p className="font-semibold text-[#0F172A]">{r.day} · {r.where}</p>
                  <p className="mt-0.5 text-xs text-slate-500">
                    {r.business_name} · {r.kind} · {r.from_time}–{r.to_time} · {r.slots} slots
                  </p>
                  {r.service_labels?.length > 0 && (
                    <p className="mt-1 text-xs text-slate-600">{r.service_labels.join(" · ")}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="mt-10" data-testid="car-public-businesses">
          <h2 className="font-display flex items-center gap-2 text-base font-bold tracking-tight text-[#0F172A] md:text-lg">
            <ShieldCheck size={18} weight="duotone" className="text-[#0B5C2E]" />
            Approved washes, valeters and garages
          </h2>
          {(board?.businesses || []).length === 0 ? (
            <p className="mt-2 text-sm text-slate-600" data-testid="car-public-businesses-empty">
              Nobody is approved round here yet. Every business on this board shows its trade
              licence number — put yours up and it goes on once an admin has checked it.
            </p>
          ) : (
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {board.businesses.map((b, i) => (
                <div key={i} className="border border-border bg-white" data-testid={`car-public-business-${i}`}>
                  {b.image_url && (
                    <img src={`${BACKEND}${b.image_url}`} alt={b.name} loading="lazy"
                      className="h-40 w-full object-cover" />
                  )}
                  <div className="p-4">
                    <p className="font-semibold text-[#0F172A]">{b.name}</p>
                    <p className="mt-0.5 text-xs text-slate-500">
                      {b.kind}{b.where ? ` · ${b.where}` : ""} · travels {b.radius_miles} miles
                    </p>
                    <p className="mt-1 text-xs text-slate-600">
                      Licence {b.licence_number}
                      {b.licence_authority ? ` · ${b.licence_authority}` : ""}
                    </p>
                    {b.service_labels?.length > 0 && (
                      <p className="mt-2 text-xs text-slate-600">{b.service_labels.join(" · ")}</p>
                    )}
                    {(b.prices || []).length > 0 && (
                      <ul className="mt-2 space-y-0.5">
                        {b.prices.map((p) => (
                          <li key={p.service} className="text-xs text-slate-600">
                            {p.label} — from {b.symbol}{Number(p.from || 0).toFixed(2)}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="mt-10 border-l-2 border-[#002FA7] bg-[#EEF3FF] px-4 py-4" data-testid="car-public-how">
          <h2 className="font-display flex items-center gap-2 text-base font-bold text-[#0F172A] md:text-lg">
            <Wrench size={18} weight="duotone" className="text-[#002FA7]" />
            How it works
          </h2>
          <ul className="mt-2 space-y-1.5 text-base leading-relaxed text-slate-700">
            <li>The owner registers the business with its trade licence and insurance number.</li>
            <li>They publish their rounds — the day, the yard and the hours they are there.</li>
            <li>You pick a slot, say where the car is parked and where the keys will be.</li>
            <li>Pay online or pay the washer on handover, at the owner's own price.</li>
            <li>Every finished job comes back with a before and an after photograph.</li>
            <li>A routine check-up sets its own reminder, so the next one is never missed.</li>
          </ul>
          <Link to="/car-care-desk" data-testid="car-public-open-desk"
            className="tap-safe mt-3 inline-flex items-center text-sm font-semibold text-[#002FA7] underline">
            Open the car care desk
          </Link>
        </section>

        <div className="mt-10">
          <NotifyMe source="car-care" testid="car-public-notify"
            blurb="Washes, mobile valeting vans and garages are joining all the time. Leave your email and the town your workplace is in, and we will tell you when somebody is doing rounds near you." />
        </div>
      </div>
    </PublicShell>
  );
}
